import { isEmpty } from 'lodash';
import { updateCartData } from '../util/cart';
import { updateCurrentUserProfile } from './user.duck';

// ================ Action types ================ //

export const SET_CART = 'app/cart/SET_CART';
export const CLEAR_CART = 'app/cart/CLEAR_CART';
export const ADD_OR_UPDATE_TO_CART_IN_PROGRESS = 'app/cart/ADD_OR_UPDATE_TO_CART_IN_PROGRESS';
export const ADD_OR_UPDATE_TO_CART_SUCCESS = 'app/cart/ADD_OR_UPDATE_TO_CART_SUCCESS';
export const ADD_OR_UPDATE_TO_CART_ERROR = 'app/cart/ADD_OR_UPDATE_TO_CART_ERROR';

export const REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_IN_PROGRESS =
  'app/cart/REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_IN_PROGRESS';
export const REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_SUCCESS =
  'app/cart/REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_SUCCESS';
export const REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_ERROR =
  'app/cart/REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_ERROR';

// ================ Reducer ================ //

const initialState = {
  cart: {},
  addOrUpdateToCartInProgressIds: [],
  addOrUpdateToCartError: null,

  removeAuthorWithDeliveryMethodFromCartInProgressIds: [],
  removeAuthorWithDeliveryMethodFromCartError: null,
};

const cartReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_CART:
      return {
        ...state,
        cart: payload,
      };

    case CLEAR_CART:
      const { cart } = state;
      if (!payload?.length) {
        return {
          ...state,
          cart: {},
        };
      }
      const newCart = removeListingsFromCart(cart, payload);

      return {
        ...state,
        cart: newCart,
      };

    case ADD_OR_UPDATE_TO_CART_IN_PROGRESS:
      return {
        ...state,
        addOrUpdateToCartInProgressIds: [...state.addOrUpdateToCartInProgressIds, payload],
        addOrUpdateToCartError: null,
      };

    case ADD_OR_UPDATE_TO_CART_SUCCESS:
      const { id, updatedCart } = payload;
      return {
        ...state,
        addOrUpdateToCartInProgressIds: state.addOrUpdateToCartInProgressIds.filter(i => i !== id),
        cart: updatedCart,
      };

    case ADD_OR_UPDATE_TO_CART_ERROR:
      return {
        ...state,
        addOrUpdateToCartInProgressIds: state.addOrUpdateToCartInProgressIds.filter(
          id => id !== payload
        ),
        addOrUpdateToCartError: payload,
      };

    case REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_IN_PROGRESS:
      return {
        ...state,
        removeAuthorWithDeliveryMethodFromCartInProgressIds: [
          ...state.removeAuthorWithDeliveryMethodFromCartInProgressIds,
          payload,
        ],
        removeAuthorWithDeliveryMethodFromCartError: null,
      };
    case REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_SUCCESS:
      return {
        ...state,
        removeAuthorWithDeliveryMethodFromCartInProgressIds: state.removeAuthorWithDeliveryMethodFromCartInProgressIds.filter(
          id => id !== payload.removeId
        ),
        cart: payload.updatedCart,
      };

    case REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_ERROR:
      return {
        ...state,
        removeAuthorWithDeliveryMethodFromCartInProgressIds: state.removeAuthorWithDeliveryMethodFromCartInProgressIds.filter(
          id => id !== payload
        ),
        removeAuthorWithDeliveryMethodFromCartError: payload,
      };

    default:
      return state;
  }
};

export default cartReducer;

// ================ Action creators ================ //

export const setCart = cart => ({
  type: SET_CART,
  payload: cart,
});

export const clearCart = listingIds => ({
  type: CLEAR_CART,
  payload: listingIds,
});

// ================ Thunks ================ //

export const removeAuthorWithDeliveryMethodFromCartInProgress = removeId => ({
  type: REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_IN_PROGRESS,
  payload: removeId,
});

export const removeAuthorWithDeliveryMethodFromCartSuccess = ({ removeId, updatedCart }) => ({
  type: REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_SUCCESS,
  payload: { removeId, updatedCart },
});

export const removeAuthorWithDeliveryMethodFromCartError = error => ({
  type: REMOVE_AUTHOR_WITH_DELIVERY_METHOD_FROM_CART_ERROR,
  payload: error,
});

export const addOrUpdateToCartInProgress = addOrUpdateId => ({
  type: ADD_OR_UPDATE_TO_CART_IN_PROGRESS,
  payload: addOrUpdateId,
});

export const addOrUpdateToCartSuccess = ({ id, updatedCart }) => ({
  type: ADD_OR_UPDATE_TO_CART_SUCCESS,
  payload: { id, updatedCart },
});

export const addOrUpdateToCartError = error => ({
  type: ADD_OR_UPDATE_TO_CART_ERROR,
  payload: error,
});

export const addOrUpdateToCart = (
  authorId,
  listingId,
  deliveryMethod,
  quantity,
  deliveryDetails,
  shouldSaveDefaultDeliveryAddress
) => async (dispatch, getState, sdk) => {
  try {
    const id = `${authorId}-${deliveryMethod}-${listingId}`;
    dispatch(addOrUpdateToCartInProgress(id));
    const { cart } = getState().cart;
    const newCart = { ...cart };
    const updatedCart = updateCartData(
      newCart,
      authorId,
      listingId,
      deliveryMethod,
      Number(quantity),
      deliveryDetails
    );
    await sdk.currentUser.updateProfile({
      privateData: { cart: updatedCart },
      ...(shouldSaveDefaultDeliveryAddress &&
      !isEmpty(deliveryDetails.shippingAddress) &&
      deliveryMethod === 'shipping'
        ? {
            publicData: {
              defaultDeliveryAddress: deliveryDetails.shippingAddress,
            },
          }
        : {}),
    });
    dispatch(addOrUpdateToCartSuccess({ id, updatedCart }));
    return updatedCart;
  } catch (error) {
    console.error({ error });
    dispatch(addOrUpdateToCartError(error));
  }
};

export const removeAuthorWithDeliveryMethodFromCart = (authorId, deliveryMethod) => async (
  dispatch,
  getState
) => {
  try {
    dispatch(removeAuthorWithDeliveryMethodFromCartInProgress(`${authorId}-${deliveryMethod}`));
    const { cart } = getState().cart;
    const newCart = { ...cart };
    delete newCart[authorId][deliveryMethod];
    if (Object.keys(newCart[authorId]).length === 0) {
      delete newCart[authorId];
    }
    await dispatch(updateCurrentUserProfile('privateData', { cart: newCart }));
    dispatch(setCart(newCart));
    dispatch(
      removeAuthorWithDeliveryMethodFromCartSuccess({
        updatedCart: newCart,
        removeId: `${authorId}-${deliveryMethod}`,
      })
    );
  } catch (error) {
    dispatch(removeAuthorWithDeliveryMethodFromCartError(error));
  }
};
